/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useState, useEffect} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {Loading} from '../../../components/Loading/Loading'
import {Input} from '../../../components/Input/Input'
import {Button} from '../../../components/Button/Button'
import {POST, GET} from '../../../services/api'
import {i18n} from '../../../translate/i18n'
import TrendChart from './components/TrendChart'

export type AnalyticsFilter = {
  startDate?: string
  endDate?: string
  categoryId?: number | string
}

interface QuestionMetrics {
  label: string
  metrics: {
    totalResponses: number
    averageScore: number
    distribution: Record<string, number>
  }
}

interface CategoryAnalytics {
  categoryId: number
  categoryTitle: string
  averageRating: number
  totalResponses: number
  questions: QuestionMetrics[]
}

interface AnalyticsData {
  totalReviews: number
  averageRating: number
  npsScore: number
  ratingDistribution: {score: number; count: number; percentage: number}[]
  trendData: {
    date: string
    averageRating: number
    totalReviews: number
    validRatings: number
    promoters: number
    detractors: number
    npsScore: number
  }[]
  categoriesAnalytics: CategoryAnalytics[]
}

interface DashboardState {
  data: AnalyticsData | null
  loading: boolean
  error: string | null
  filters: AnalyticsFilter
}

const Content: FC = () => {
  const [state, setState] = useState<DashboardState>({
    data: null,
    loading: true,
    error: null,
    filters: {},
  })

  const [categoryOptions, setCategoryOptions] = useState<
    Array<{value: number | string; select: string}>
  >([])
  const [dateStart, setDateStart] = useState<string>('')
  const [dateEnd, setDateEnd] = useState<string>('')
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | ''>('')
  const [timeGrouping, setTimeGrouping] = useState<'day' | 'month' | 'year'>('day')

  useEffect(() => {
    fetchCategoriesForFilter()
    loadAnalytics({})
  }, [])

  const fetchCategoriesForFilter = async () => {
    try {
      const res = await GET('/review-hotel-category')
      if (res.data) {
        const options = res.data.map((c: any) => ({
          value: c.id,
          select: c.title,
        }))
        setCategoryOptions([{value: '', select: 'Todas as Categorias'}, ...options])
      }
    } catch (e) {
      console.error('Erro ao buscar categorias para filtro', e)
    }
  }

  const loadAnalytics = async (filters: AnalyticsFilter) => {
    setState((prev) => ({...prev, loading: true, error: null, filters}))
    try {
      const response = await POST('/review-hotel/analytics', {
        dateFrom: filters.startDate,
        dateTo: filters.endDate,
        categoryId: filters.categoryId,
      })
      if (response.success && response.data) {
        setState((prev) => ({...prev, data: response.data, loading: false}))
      } else {
        throw new Error(response.message || 'Erro ao carregar analytics')
      }
    } catch (error: any) {
      setState((prev) => ({...prev, loading: false, error: error.message}))
    }
  }

  const handleApplyFilters = () => {
    const newFilters: AnalyticsFilter = {}
    if (dateStart) newFilters.startDate = new Date(`${dateStart}T00:00:00`).toISOString()
    if (dateEnd) newFilters.endDate = new Date(`${dateEnd}T23:59:59.999`).toISOString()
    if (selectedCategoryId) newFilters.categoryId = Number(selectedCategoryId)
    loadAnalytics(newFilters)
  }

  const handleClearFilters = () => {
    setDateStart('')
    setDateEnd('')
    setSelectedCategoryId('')
    loadAnalytics({})
  }

  const getAggregatedTrendData = () => {
    if (!state.data?.trendData) return []

    const grouped = state.data.trendData.reduce((acc: any, curr: any) => {
      let key = curr.date
      if (timeGrouping === 'month') key = curr.date.substring(0, 7)
      if (timeGrouping === 'year') key = curr.date.substring(0, 4)

      if (!acc[key]) {
        acc[key] = {
          date: key,
          totalReviews: 0,
          validRatings: 0,
          sumRating: 0,
          promoters: 0,
          detractors: 0,
        }
      }

      const validCount = curr.validRatings !== undefined ? curr.validRatings : curr.totalReviews
      acc[key].totalReviews += curr.totalReviews
      acc[key].validRatings += validCount
      acc[key].sumRating += curr.averageRating * validCount
      acc[key].promoters += curr.promoters || 0
      acc[key].detractors += curr.detractors || 0
      return acc
    }, {})

    return Object.values(grouped)
      .map((g: any) => {
        let nps = 0
        if (g.validRatings > 0)
          nps = Math.round(((g.promoters - g.detractors) / g.validRatings) * 100)
        return {
          date: g.date,
          totalReviews: g.totalReviews,
          averageRating: g.validRatings > 0 ? g.sumRating / g.validRatings : 0,
          npsScore: nps,
        }
      })
      .sort((a: any, b: any) => a.date.localeCompare(b.date))
  }

  const renderDynamicQuestionMetric = (q: QuestionMetrics, index: number) => {
    return (
      <div key={index} className='mb-6 pb-6 border-bottom border-gray-200'>
        <div className='d-flex align-items-center justify-content-between mb-4'>
          <div className='d-flex align-items-center gap-3'>
            <h5 className='text-dark fw-bolder m-0'>{q.label}</h5>
          </div>
          <span className='text-muted fs-8 fw-bold bg-light px-3 py-1 rounded'>
            {q.metrics.totalResponses} respostas
          </span>
        </div>

        {/* Avaliação em Estrelas */}
        {q.metrics.averageScore > 0 && (
          <div className='d-inline-flex align-items-center mt-2 px-4 py-3 bg-light-warning rounded mb-4'>
            <i className='fas fa-star fs-2 text-warning me-3'></i>
            <span className='fs-2 fw-bolder text-warning'>
              {q.metrics.averageScore.toFixed(1)}{' '}
              <span className='fs-6 text-warning opacity-75 fw-bold'>/ 5.0</span>
            </span>
          </div>
        )}

        {/* Comentários de Texto */}
        {q.metrics.distribution && Object.keys(q.metrics.distribution).length > 0 ? (
          <div className='d-flex flex-column gap-3 mt-4'>
            <h6 className='text-muted fs-7 text-uppercase fw-bold mb-1'>Comentários:</h6>
            {Object.entries(q.metrics.distribution)
              .sort(([, valA], [, valB]) => (valB as number) - (valA as number))
              .map(([textComment, count], i) => (
                <div key={i} className='bg-light-info rounded p-4 border border-info border-dashed'>
                  <div className='text-gray-800 fs-6 fst-italic'>"{textComment}"</div>
                  {Number(count) > 1 && (
                    <div className='text-info fw-bolder fs-8 mt-2'>
                      <i className='fas fa-reply me-1 text-info'></i> Citado {String(count)} vezes
                    </div>
                  )}
                </div>
              ))}
          </div>
        ) : (
          <div className='text-muted fs-8 mt-2 fst-italic'>
            Nenhum comentário por escrito nesta avaliação.
          </div>
        )}
      </div>
    )
  }

  const getNpsColor = (score: number) => {
    if (score >= 75) return 'text-success'
    if (score >= 50) return 'text-warning'
    return 'text-danger'
  }

  const getNpsBgColor = (score: number) => {
    if (score >= 75) return 'bg-light-success'
    if (score >= 50) return 'bg-light-warning'
    return 'bg-light-danger'
  }

  if (state.loading) return <Loading />
  const chartData = getAggregatedTrendData()

  return (
    <div className='row g-5 gx-xxl-12'>
      <div className='card card-xxl-stretch mb-5 mb-xl-12'>
        <div className='card-header py-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Dashboard Analytics</span>
            <BreadCrumbs />
            <span className='text-muted mt-1 fw-bold fs-7'>
              Métricas extraídas das avaliações dos hóspedes
            </span>
          </h3>
        </div>

        <div className='card-body border-bottom'>
          {state.error && (
            <div className='alert alert-danger d-flex align-items-center p-5 mb-10'>
              <i className='fas fa-exclamation-triangle fs-2hx text-danger me-4'></i>
              <div className='d-flex flex-column'>
                <h4 className='mb-1 text-danger'>Erro ao carregar dados</h4>
                <span>{state.error}</span>
              </div>
            </div>
          )}

          <div className='row g-3 mt-2'>
            <div className='col-md-4 mt-0'>
              <Input
                label='Categoria'
                inputAttr={{type: 'select'}}
                value={selectedCategoryId}
                change={(v: number) => setSelectedCategoryId(v)}
                options={
                  categoryOptions.length > 0
                    ? categoryOptions
                    : [{value: '', select: 'Carregando...'}]
                }
              />
            </div>
            <div className='col-md-4 mt-0'>
              <Input
                label='Data Inicial'
                inputAttr={{type: 'date'}}
                value={dateStart}
                change={setDateStart}
                marginTop='0'
              />
            </div>
            <div className='col-md-4 mt-0'>
              <Input
                label='Data Final'
                inputAttr={{type: 'date'}}
                value={dateEnd}
                change={setDateEnd}
                marginTop='0'
              />
            </div>
            <div className='col-12 d-flex justify-content-end mt-4'>
              <Button
                text='Limpar'
                color='danger'
                iconColor='#FFF'
                size='small'
                horizontalPadding={4}
                btnAttr={{type: 'button'}}
                click={handleClearFilters}
                btnClass='me-2'
              />
              <Button
                text='Filtrar'
                color='primary'
                icon='fas fa-filter'
                iconColor='#FFF'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={handleApplyFilters}
                btnClass='mb-0 fw-bolder'
              />
            </div>
          </div>
        </div>

        <div className='card-body bg-light'>
          {state.data && (
            <>
              {/* Visão Geral (Cards) */}
              <div className='row g-5 mb-8'>
                <div className='col-xl-4'>
                  <div className='card bg-body shadow-sm border-0 h-100'>
                    <div className='card-body d-flex flex-column justify-content-center'>
                      <div className='d-flex align-items-center justify-content-between mb-4'>
                        <span className='text-gray-600 fw-bold fs-5'>Total de Avaliações</span>
                        <i className='fas fa-comments fs-2x text-primary'></i>
                      </div>
                      <span className='text-dark fw-bolder fs-2tx'>{state.data.totalReviews}</span>
                    </div>
                  </div>
                </div>

                <div className='col-xl-4'>
                  <div className='card bg-body shadow-sm border-0 h-100'>
                    <div className='card-body d-flex flex-column justify-content-center'>
                      <div className='d-flex align-items-center justify-content-between mb-4'>
                        <span className='text-gray-600 fw-bold fs-5'>Média Geral</span>
                        <i className='fas fa-star fs-2x text-warning'></i>
                      </div>
                      <div className='d-flex align-items-baseline'>
                        <span className='text-dark fw-bolder fs-2tx'>
                          {state.data.averageRating > 0
                            ? state.data.averageRating.toFixed(1)
                            : 'N/A'}
                        </span>
                        {state.data.averageRating > 0 && (
                          <span className='text-muted fw-bold fs-3 ms-2'>/ 5.0</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className='col-xl-4'>
                  <div className={`card ${getNpsBgColor(state.data.npsScore)} border-0 h-100`}>
                    <div className='card-body'>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <span className={`${getNpsColor(state.data.npsScore)} fw-bold fs-5`}>
                          Score NPS
                          <i
                            className='fas fa-info-circle ms-2 fs-7 text-muted'
                            title='Termômetro de satisfação (Baseado em 5 estrelas).'
                            data-bs-toggle='tooltip'
                          ></i>
                        </span>
                        <i
                          className={`fas fa-tachometer-alt fs-2 ${getNpsColor(
                            state.data.npsScore
                          )} opacity-50`}
                        ></i>
                      </div>
                      <span className='text-dark fw-bolder fs-2tx'>
                        {state.data.totalReviews === 0 ? (
                          <span className='text-muted'>N/A</span>
                        ) : (
                          state.data.npsScore
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Distribuição e Gráfico */}
              <div className='row g-5 mb-8'>
                <div className='col-xl-12'>
                  <div className='card border border-gray-300 shadow-none'>
                    <div className='card-header pt-5 border-0'>
                      <h3 className='card-title flex-column m-0'>
                        <span className='card-label fw-bolder text-dark'>
                          Distribuição de Notas (1 a 5)
                        </span>
                      </h3>
                    </div>
                    <div className='card-body pt-5'>
                      {state.data.ratingDistribution
                        .sort((a, b) => b.score - a.score)
                        .map((item) => (
                          <div key={item.score} className='d-flex align-items-center mb-4'>
                            <div className='w-40px fw-bolder text-gray-800'>
                              {item.score} <i className='fas fa-star text-warning fs-8'></i>
                            </div>
                            <div className='flex-grow-1 mx-3'>
                              <div className='progress h-8px bg-light-primary'>
                                <div
                                  className='progress-bar bg-warning rounded'
                                  role='progressbar'
                                  style={{width: `${item.percentage}%`}}
                                ></div>
                              </div>
                            </div>
                            <div className='w-40px text-end fw-bolder text-muted'>{item.count}</div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>

                <div className='col-xl-12'>
                  <div className='card border border-gray-300 shadow-none'>
                    <div className='card-header pt-5 border-0 align-items-center d-flex justify-content-between'>
                      <h3 className='card-title flex-column m-0'>
                        <span className='card-label fw-bolder text-dark'>Evolução Histórica</span>
                        <span className='text-muted mt-1 fw-bold fs-7'>
                          Volume de respostas e variação da média
                        </span>
                      </h3>
                      <div className='card-toolbar'>
                        <div className='btn-group' role='group'>
                          <button
                            type='button'
                            className={`btn btn-sm ${
                              timeGrouping === 'day' ? 'btn-primary' : 'btn-light-primary'
                            }`}
                            onClick={() => setTimeGrouping('day')}
                          >
                            {' '}
                            Dia{' '}
                          </button>
                          <button
                            type='button'
                            className={`btn btn-sm ${
                              timeGrouping === 'month' ? 'btn-primary' : 'btn-light-primary'
                            }`}
                            onClick={() => setTimeGrouping('month')}
                          >
                            {' '}
                            Mês{' '}
                          </button>
                          <button
                            type='button'
                            className={`btn btn-sm ${
                              timeGrouping === 'year' ? 'btn-primary' : 'btn-light-primary'
                            }`}
                            onClick={() => setTimeGrouping('year')}
                          >
                            {' '}
                            Ano{' '}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className='card-body'>
                      {chartData && chartData.length > 0 ? (
                        <div style={{minHeight: '350px'}}>
                          <TrendChart data={chartData} grouping={timeGrouping} />
                        </div>
                      ) : (
                        <div className='text-center text-muted py-10'>
                          Dados insuficientes para gerar a curva de tendência.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabela Resumo */}
              <div className='row g-5 mb-8'>
                <div className='col-xl-12'>
                  <div className='card border border-gray-300 shadow-sm'>
                    <div className='card-header pt-5 border-0'>
                      <h3 className='card-title flex-column m-0'>
                        <span className='card-label fw-bolder text-dark'>
                          Desempenho por Categoria
                        </span>
                      </h3>
                    </div>
                    <div className='card-body py-3'>
                      <div className='table-responsive'>
                        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                          <thead>
                            <tr className='fw-bolder text-muted bg-light'>
                              <th className='ps-4 rounded-start'>Categoria</th>
                              <th className='text-center'>Avaliações</th>
                              <th className='text-end pe-4 rounded-end'>Nota Média</th>
                            </tr>
                          </thead>
                          <tbody>
                            {state.data.categoriesAnalytics?.length > 0 ? (
                              state.data.categoriesAnalytics.map((cat, idx) => (
                                <tr key={idx}>
                                  <td className='ps-4 fw-bolder text-gray-800'>
                                    {cat.categoryTitle}
                                  </td>
                                  <td className='text-center fw-bolder'>{cat.totalResponses}</td>
                                  <td className='text-end pe-4'>
                                    {cat.averageRating > 0 ? (
                                      <span
                                        className={`badge ${
                                          cat.averageRating >= 4
                                            ? 'badge-light-success'
                                            : cat.averageRating >= 3
                                            ? 'badge-light-warning'
                                            : 'badge-light-danger'
                                        } fs-6 d-inline-flex align-items-center`}
                                      >
                                        <i className='fas fa-star text-warning me-2 fs-8'></i>{' '}
                                        {cat.averageRating.toFixed(1)}
                                      </span>
                                    ) : (
                                      '-'
                                    )}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={3} className='text-center text-muted py-8'>
                                  Nenhum dado de categoria disponível.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ACCORDION: Comentários e Respostas Detalhadas */}
              <div className='card border border-gray-300 shadow-sm'>
                <div className='card-header pt-5 border-0 mb-5'>
                  <h3 className='card-title flex-column m-0'>
                    <span className='card-label fw-bolder text-dark'>Respostas Detalhadas</span>
                    <span className='text-muted mt-1 fw-bold fs-7'>
                      Avaliações e comentários separados por categoria e pergunta
                    </span>
                  </h3>
                </div>

                <div className='card-body pt-0'>
                  <div className='accordion accordion-icon-toggle' id='accordionMetrics'>
                    {state.data.categoriesAnalytics.map((cat, idx) => (
                      <div
                        className='accordion-item mb-5 border-top border-gray-200'
                        key={`acc-${cat.categoryId}`}
                      >
                        <h2 className='accordion-header' id={`heading-${idx}`}>
                          <button
                            className='accordion-button collapsed py-4'
                            type='button'
                            data-bs-toggle='collapse'
                            data-bs-target={`#collapse-${idx}`}
                          >
                            <div className='d-flex align-items-center justify-content-between w-100 me-4'>
                              <div className='d-flex align-items-center'>
                                <i className='fas fa-file-alt text-primary fs-3 me-3'></i>
                                <span className='fw-bolder fs-4 text-dark'>
                                  {cat.categoryTitle}
                                </span>
                              </div>
                              <span className='badge badge-light-primary'>
                                {cat.totalResponses} avaliações
                              </span>
                            </div>
                          </button>
                        </h2>
                        <div
                          id={`collapse-${idx}`}
                          className='accordion-collapse collapse'
                          data-bs-parent='#accordionMetrics'
                        >
                          <div className='accordion-body bg-white pt-8 pb-2'>
                            {cat.questions && cat.questions.length > 0 ? (
                              cat.questions.map((q, qIndex) =>
                                renderDynamicQuestionMetric(q, qIndex)
                              )
                            ) : (
                              <div className='text-muted py-4 text-center'>
                                Nenhuma avaliação detalhada registrada.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const ReviewHotelAnalyticsIndex: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {title: 'Avaliações de Hóspedes', path: 'review-hotel', isActive: false},
          {title: 'Analytics', path: 'analytics', isActive: true},
        ]}
      >
        Dashboard Analytics
      </PageTitle>
      <Content />
    </>
  )
}

export {ReviewHotelAnalyticsIndex}
